import localization from '../../../../shared/strings';
import { buildMasterVariantDisplayName } from '../../../ProductInfo/helpers/buildMasterVariantDisplayName';

const normalizeLabel = (value) => String(value || '').trim();

const isProductLevelTitle = (label, productTitle) => {
  const normalizedLabel = normalizeLabel(label);
  const normalizedProductTitle = normalizeLabel(productTitle);

  if (!normalizedLabel || !normalizedProductTitle) return false;

  return normalizedLabel === normalizedProductTitle;
};

const dedupeLabels = (labels = []) => {
  const seen = new Set();
  const result = [];

  labels.forEach((label) => {
    const normalizedLabel = normalizeLabel(label);
    if (!normalizedLabel || seen.has(normalizedLabel)) return;

    seen.add(normalizedLabel);
    result.push(normalizedLabel);
  });

  return result;
};

const resolveVariantOptionLabel = (variantOptions = {}, availableOptions = []) => {
  const optionValues = availableOptions
    .map(({ name }) => variantOptions[name])
    .filter((value) => value != null && value !== '');

  if (!optionValues.length) return '';

  return optionValues.join(' / ');
};

const findMasterProductForVariant = (products, masterVariantId, masterProductId) => {
  if (masterProductId) {
    const productById = products.find(({ _id }) => String(_id) === String(masterProductId));
    if (productById) return productById;
  }

  return products.find((product) => (
    product?.variants?.some(({ _id }) => String(_id) === String(masterVariantId))
  )) || null;
};

const resolveMasterVariantLabel = (masterVariant, masterProduct, productTitle) => {
  if (!masterVariant) return '';

  const optionLabel = resolveVariantOptionLabel(
    masterVariant.options,
    masterProduct?.availableOptions,
  );

  if (optionLabel && !isProductLevelTitle(optionLabel, productTitle)) {
    return optionLabel;
  }

  if (masterVariant.displayName && !isProductLevelTitle(masterVariant.displayName, productTitle)) {
    return masterVariant.displayName;
  }

  if (masterVariant.title && !isProductLevelTitle(masterVariant.title, productTitle)) {
    return masterVariant.title;
  }

  const fullDisplayName = buildMasterVariantDisplayName({
    productTitle: masterProduct?.title || '',
    availableOptions: masterProduct?.availableOptions || [],
    variantOptions: masterVariant.options || {},
  });

  if (fullDisplayName && !isProductLevelTitle(fullDisplayName, productTitle)) {
    return fullDisplayName;
  }

  return '';
};

export const buildSharedAltAffectedUsages = ({
  usedByProduct,
  variantLabels = [],
}) => {
  const usages = [];

  if (usedByProduct) {
    usages.push(localization.ALT_TEXT.productGallery);
  }

  return [...usages, ...dedupeLabels(variantLabels)];
};

export const formatSharedAltUsagesText = ({
  usedByProduct,
  productTitle,
  variantLabels = [],
  maxVisible,
}) => {
  const parts = dedupeLabels([
    ...(usedByProduct
      ? [productTitle || localization.ALT_TEXT.productGallery]
      : []),
    ...variantLabels,
  ]);

  if (!parts.length) return '';

  if (!maxVisible || parts.length <= maxVisible) {
    return parts.join(' and ');
  }

  const visibleParts = parts.slice(0, maxVisible);
  const hiddenCount = parts.length - maxVisible;

  return `${visibleParts.join(' and ')} and ${hiddenCount} more`;
};

export const resolveVariantLabel = (variantId, {
  products = [],
  listingVariants = {},
  productVariantLinks = [],
  productTitle = '',
} = {}) => {
  if (!variantId) return '';

  const matchingLink = productVariantLinks.find(
    (link) => String(link.variantId) === String(variantId),
  );

  const listingVariant = listingVariants[variantId]
    || Object.values(listingVariants).find(
      (entry) => String(entry?._id) === String(variantId),
    );

  if (listingVariant) {
    const masterProduct = findMasterProductForVariant(
      products,
      listingVariant.masterVariantId,
      listingVariant.masterProductId || matchingLink?.masterProductId,
    );

    const listingOptionLabel = resolveVariantOptionLabel(
      listingVariant.options,
      masterProduct?.availableOptions,
    );

    if (listingOptionLabel && !isProductLevelTitle(listingOptionLabel, productTitle)) {
      return listingOptionLabel;
    }

    if (listingVariant.masterVariantId) {
      const masterVariantLabel = resolveMasterVariantLabel(
        masterProduct?.variants?.find(
          ({ _id }) => String(_id) === String(listingVariant.masterVariantId),
        ),
        masterProduct,
        productTitle,
      );

      if (masterVariantLabel) return masterVariantLabel;
    }

    if (listingVariant.displayName && !isProductLevelTitle(listingVariant.displayName, productTitle)) {
      return listingVariant.displayName;
    }

    if (listingVariant.title && !isProductLevelTitle(listingVariant.title, productTitle)) {
      return listingVariant.title;
    }
  }

  if (
    matchingLink?.title
    && String(matchingLink.title) !== String(variantId)
    && !isProductLevelTitle(matchingLink.title, productTitle)
  ) {
    return matchingLink.title;
  }

  for (const product of products) {
    const masterVariant = product?.variants?.find(
      ({ _id }) => String(_id) === String(variantId),
    );

    const masterVariantLabel = resolveMasterVariantLabel(masterVariant, product, productTitle);
    if (masterVariantLabel) return masterVariantLabel;
  }

  const masterVariant = products
    ?.flatMap((product) => product?.variants || [])
    .find(({ _id, listingVariantId }) => (
      String(_id) === String(variantId) || String(listingVariantId) === String(variantId)
    ));

  return resolveMasterVariantLabel(masterVariant, findMasterProductForVariant(
    products,
    masterVariant?._id,
    masterVariant?.productId,
  ), productTitle);
};

export const resolvePendingOwnerLabel = ({
  pendingOwner,
  products,
  listingId,
  listingVariants = {},
  productTitle = '',
  asset = null,
}) => {
  if (!pendingOwner?.id) return '';

  if (pendingOwner.type === 'listing') {
    const listing = products
      ?.flatMap((product) => product?.channels || [])
      .find((channel) => String(channel?.listingProduct?._id) === String(listingId));

    return listing?.listingProduct?.title
      || products.find(({ _id }) => String(_id) === String(listingId))?.title
      || localization.ALT_TEXT.productGallery;
  }

  const productVariantLinks = (asset?.productVariants || []).filter(
    (link) => !listingId || String(link.productId) === String(listingId),
  );

  return resolveVariantLabel(pendingOwner.id, {
    products,
    listingVariants,
    productVariantLinks,
    productTitle,
  }) || '';
};

export const buildSharedVariantLabels = ({
  usedByVariants = [],
  products,
  listingId,
  asset,
  listingVariants = {},
  productTitle = '',
}) => {
  const productVariantLinks = (asset?.productVariants || []).filter(
    (link) => !listingId || String(link.productId) === String(listingId),
  );

  return dedupeLabels(
    usedByVariants.map((variantId) => resolveVariantLabel(variantId, {
      products,
      listingVariants,
      productVariantLinks,
      productTitle,
    })).filter(Boolean),
  );
};
